import React from "react";
import { Link } from "@/i18n/router";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Minus } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { SectionHeading } from "@/components/ui/primitives";

export function Blocks({ blocks }) {
  return (
    <>
      {blocks.map((b, i) => {
        const alt = i % 2 === 1;
        const wrap = `py-20 lg:py-28 ${alt ? "bg-navy-surface border-y border-navy-border" : "bg-navy"}`;
        return (
          <section key={i} className={wrap} aria-label={b.title || b.type}>
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
              {(b.overline || b.title) && (
                <SectionHeading overline={b.overline} title={b.title} sub={b.sub} />
              )}
              <div className={b.overline || b.title ? "mt-12" : ""}>{renderBody(b)}</div>
            </div>
          </section>
        );
      })}
    </>
  );
}

function renderBody(b) {
  switch (b.type) {
    case "intro":
      return (
        <Reveal>
          <div className="grid gap-8 md:grid-cols-2">
            {b.paragraphs.map((p, i) => (
              <p key={i} className="text-lg leading-relaxed text-silver/75">{p}</p>
            ))}
          </div>
        </Reveal>
      );
    case "grid":
      return (
        <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {b.items.map((it) => (
            <motion.div key={it.title} variants={staggerItem} className="rounded-2xl border border-navy-border bg-navy p-7 transition-colors hover:border-astro-blue/40">
              {it.icon && (
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-astro-blue/10 text-astro-light">
                  <it.icon className="h-5 w-5" />
                </div>
              )}
              <h3 className="mt-5 font-heading text-lg font-semibold text-white">{it.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-silver/60">{it.text}</p>
            </motion.div>
          ))}
        </Stagger>
      );
    case "steps":
      return (
        <div className="space-y-3">
          {b.items.map((it, i) => (
            <Reveal key={it.title} delay={i * 0.04}>
              <div className="flex items-start gap-5 rounded-2xl border border-navy-border bg-navy p-6">
                <span className="font-heading text-2xl font-black text-outline w-12 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-white">{it.title}</h3>
                  <p className="mt-1 text-sm text-silver/60">{it.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      );
    case "accordion":
      return (
        <Reveal>
          <Accordion type="single" collapsible className="mx-auto max-w-3xl">
            {b.items.map((it, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-navy-border">
                <AccordionTrigger className="text-left font-heading text-base font-semibold text-white hover:text-astro-light hover:no-underline">
                  {it.q}
                </AccordionTrigger>
                <AccordionContent className="text-silver/70 leading-relaxed">{it.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      );
    case "definitions":
      return (
        <Stagger className="grid gap-4 md:grid-cols-2">
          {b.items.map((it) => (
            <motion.div key={it.term} variants={staggerItem} className="rounded-xl border border-navy-border bg-navy p-6">
              <dt className="font-heading text-base font-semibold text-astro-light">{it.term}</dt>
              <dd className="mt-2 text-sm leading-relaxed text-silver/70">{it.def}</dd>
            </motion.div>
          ))}
        </Stagger>
      );
    case "linkcards":
      return (
        <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {b.items.map((it) => {
            const inner = (
              <>
                {it.icon && <it.icon className="h-7 w-7 text-astro-light" />}
                <h3 className="mt-5 font-heading text-lg font-semibold text-white">{it.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-silver/60">{it.text}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-astro-light">
                  {it.cta || "Learn more"} {it.href ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />}
                </span>
              </>
            );
            const cls = "group flex h-full flex-col rounded-2xl border border-navy-border bg-navy p-7 transition-all duration-300 hover:-translate-y-1 hover:border-astro-blue/40";
            return (
              <motion.div key={it.title} variants={staggerItem}>
                {it.href ? (
                  <a href={it.href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
                ) : (
                  <Link to={it.to || "#"} className={cls}>{inner}</Link>
                )}
              </motion.div>
            );
          })}
        </Stagger>
      );
    case "stats":
      return (
        <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {b.items.map((it) => (
            <motion.div key={it.label} variants={staggerItem} className="rounded-2xl border border-navy-border bg-navy p-7 text-center">
              <p className="font-heading text-4xl font-black text-white">{it.value}</p>
              <p className="mt-2 text-sm text-silver/60">{it.label}</p>
            </motion.div>
          ))}
        </Stagger>
      );
    case "comparison":
      return (
        <Reveal>
          <div className="overflow-hidden rounded-2xl border border-navy-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-navy-surface">
                <tr className="border-b border-navy-border">
                  <th className="px-5 py-4 font-heading text-white">Capability</th>
                  <th className="px-5 py-4 font-heading text-astro-light">{b.us || "AstroLife AI Governance OS"}</th>
                  <th className="px-5 py-4 font-heading text-silver/60">{b.them || "Generic solutions"}</th>
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r) => (
                  <tr key={r.capability} className="border-b border-navy-border/50 bg-navy">
                    <td className="px-5 py-4 font-medium text-white">{r.capability}</td>
                    <td className="px-5 py-4">{cell(r.us, true)}</td>
                    <td className="px-5 py-4">{cell(r.them, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      );
    default:
      return null;
  }
}

function cell(val, positive) {
  if (val === true) return <Check className="h-5 w-5 text-emerald-400" />;
  if (val === false) return <Minus className="h-5 w-5 text-silver/30" />;
  return <span className={positive ? "text-silver/85" : "text-silver/50"}>{val}</span>;
}
